import 'focus-visible/dist/focus-visible';

import { ChakraProvider, Grid } from '@chakra-ui/react';
import type { DehydratedState } from '@tanstack/react-query';
import {
  HydrationBoundary,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { ClientOnly, Fonts } from '@time-line/components/common';
import { TLComponentsProvider } from '@time-line/components/hooks';
import { Analytics } from '@vercel/analytics/react';
import { SpeedInsights } from '@vercel/speed-insights/next';
import { Cart } from 'components/cart/Cart';
import { QueryParamDiscountHandler } from 'components/cart/QueryParamDiscountHandler';
import { FullscreenPopup } from 'components/common/FullscreenPopup';
import { JsonLd } from 'components/common/JsonLd';
import { ToastNotifications } from 'components/common/ToastNotifications';
import { CampaignFooter } from 'components/footers/CampaignFooter';
import { Footer } from 'components/footers/Footer';
import { Header } from 'components/Header/Header';
import { CountryModal } from 'components/localisation/CountryModal';
import { QueryParamLocaleSelector } from 'components/localisation/QueryParamLocaleSelector';
import { useCartABTests } from 'hooks/cart/useCartABTests';
import { useResetLocale } from 'hooks/localisation/useResetLocale';
import { useCookiebotConsent } from 'hooks/useCookiebotConsent';
import { useIsCampaignPage } from 'hooks/useIsCampaignPage';
import { useIsPromotionV2Page } from 'hooks/useIsPromotionV2Page';
import { isSkincarePath } from 'hooks/useIsSkincare';
import { PolarSdkSession } from 'hooks/usePolarIdentity';
import { useRouteChange } from 'hooks/useRouteChange';
import { useScriptManager } from 'hooks/useScriptManager';
import { cloudinary as cloudinaryClient } from 'lib/cloudinary';
import { env, isProd } from 'lib/constants/env';
import { DEFAULT_COUNTRY_CODE } from 'lib/constants/siteDefaults';
import { client as sanityClient } from 'lib/sanity';
import type { AppProps as NextAppProps } from 'next/app';
import Head from 'next/head';
import { useRouter } from 'next/router';
import type { Session } from 'next-auth';
import { SessionProvider } from 'next-auth/react';
import { DefaultSeo } from 'next-seo';
import { useState } from 'react';
import { useCookie } from 'react-use';
import { baseTheme } from 'theme';
import { buildOrganization, buildWebSite } from 'utils/jsonld';

import SEO from '../next-seo.config';

const ORGANIZATION_JSONLD = buildOrganization();
const WEBSITE_JSONLD = buildWebSite();

interface PageProps {
  key: string;
  slug: string;
  session: Session;
  dehydratedState?: DehydratedState;
}

type AppProps<P = any> = {
  pageProps: P;
} & Omit<NextAppProps<P>, 'pageProps'>;

function App({ Component, pageProps }: AppProps<PageProps>) {
  const { key, dehydratedState, session, ...props } = pageProps;

  const router = useRouter();
  const [usersCountryCode] = useCookie('countryCode');
  const isUS = usersCountryCode === DEFAULT_COUNTRY_CODE;

  const { isConsented } = useCookiebotConsent();

  const { elements: scriptElements } = useScriptManager({
    cookiesAccepted: isConsented,
    isUS,
    isVibeGalleryOnPage: isSkincarePath(router.asPath),
  });

  useRouteChange();
  useResetLocale();

  const isCampaignPage = useIsCampaignPage();
  const isPromotionV2Page = useIsPromotionV2Page();
  const cartABTests = useCartABTests();

  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            refetchOnWindowFocus: false,
            refetchOnMount: false,
          },
        },
      }),
  );

  return (
    <>
      <Head>
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, viewport-fit=cover"
        />
      </Head>
      <Analytics />
      {scriptElements}
      <QueryClientProvider client={queryClient}>
        <TLComponentsProvider
          sanityClient={sanityClient}
          cloudinaryClient={cloudinaryClient}
        >
          <HydrationBoundary state={dehydratedState}>
            <SessionProvider session={session}>
              <DefaultSeo {...SEO(router.asPath)} />
              <JsonLd
                scriptKey="sitewide-organization"
                schema={ORGANIZATION_JSONLD}
              />
              <JsonLd scriptKey="sitewide-website" schema={WEBSITE_JSONLD} />
              <Fonts />
              <ChakraProvider theme={baseTheme}>
                <ClientOnly>
                  <Cart {...cartABTests} />
                </ClientOnly>
                <ToastNotifications />
                {env.NEXT_PUBLIC_COUNTRY_SELECTOR_ENABLED && (
                  <>
                    <CountryModal />
                    <QueryParamLocaleSelector />
                  </>
                )}
                {env.NEXT_PUBLIC_POLAR_PIXEL_ENDPOINT && <PolarSdkSession />}
                <QueryParamDiscountHandler />
                <FullscreenPopup />
                <Grid gridTemplateRows="auto 1fr auto" minHeight="100dvh">
                  {!isPromotionV2Page && <Header />}
                  <Component key={key} {...props} />
                  {isCampaignPage ? <CampaignFooter /> : <Footer />}
                </Grid>
              </ChakraProvider>
            </SessionProvider>
          </HydrationBoundary>
        </TLComponentsProvider>
        <ReactQueryDevtools />
      </QueryClientProvider>
      {isProd && <SpeedInsights sampleRate={0.1} />}
    </>
  );
}

export default App;
